import React, { useState, useEffect } from 'react';
import { X, FolderOpen, Check, Plus, Loader2 } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import api from '../../utils/api';

const AddToCollectionModal = ({
    isOpen,
    onClose,
    file,
    onSuccess
}) => {
    const [collections, setCollections] = useState([]);
    const [selectedCollections, setSelectedCollections] = useState([]);
    const [existingCollections, setExistingCollections] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [showCreate, setShowCreate] = useState(false);
    const [newName, setNewName] = useState('');

    useEffect(() => {
        if (isOpen && file) {
            fetchCollections();
            fetchFileCollections();
        }
    }, [isOpen, file]);

    const fetchCollections = async () => {
        try {
            const response = await api.get('/collections');
            if (response.data.collections) {
                setCollections(response.data.collections);
            }
        } catch (error) {
            console.error('Error fetching collections:', error);
        } finally {
            setLoading(false);
        }
    };

    const fetchFileCollections = async () => {
        try {
            const response = await api.get(`/collections/file/${file.id}`);
            if (response.data.collections) {
                const ids = response.data.collections.map(c => c.id);
                setExistingCollections(ids);
                setSelectedCollections(ids);
            }
        } catch (error) {
            console.error('Error fetching file collections:', error);
        }
    };

    const getIconComponent = (iconName) => {
        const formattedName = (iconName || 'folder-open')
            .split('-')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join('');
        return LucideIcons[formattedName] || FolderOpen;
    };

    const handleToggle = (collectionId) => {
        setSelectedCollections(prev =>
            prev.includes(collectionId)
                ? prev.filter(id => id !== collectionId)
                : [...prev, collectionId]
        );
    };

    const handleCreateCollection = async () => {
        if (!newName.trim()) return;

        try {
            const response = await api.post('/collections',
                { name: newName.trim() }
            );
            if (response.data.collection) {
                const newCollection = response.data.collection;
                setCollections(prev => [...prev, newCollection]);
                setSelectedCollections(prev => [...prev, newCollection.id]);
                setNewName('');
                setShowCreate(false);
            }
        } catch (error) {
            console.error('Error creating collection:', error);
        }
    };

    const handleSave = async () => {
        setSaving(true);

        try {
            // Collections to add (in selected but not existing)
            const toAdd = selectedCollections.filter(id => !existingCollections.includes(id));
            // Collections to remove (in existing but not selected)
            const toRemove = existingCollections.filter(id => !selectedCollections.includes(id));

            // Add to new collections
            for (const collectionId of toAdd) {
                await api.post(`/collections/${collectionId}/files`,
                    { fileIds: [file.id] }
                );
            }

            // Remove from collections
            for (const collectionId of toRemove) {
                await api.delete(`/collections/${collectionId}/files/${file.id}`);
            }

            onSuccess?.();
            onClose();
        } catch (error) {
            console.error('Error updating collections:', error);
        } finally {
            setSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />

            <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden">
                {/* Header */}
                <div className="p-5 border-b border-gray-100">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-bold text-gray-900">Add to Collection</h2>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                        >
                            <X size={18} className="text-gray-500" />
                        </button>
                    </div>
                    <p className="text-sm text-gray-500 mt-1">
                        Select collections for "{file?.file_name}"
                    </p>
                </div>

                {/* Collections List */}
                <div className="p-4 max-h-[50vh] overflow-y-auto">
                    {loading ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 size={24} className="animate-spin text-primary" />
                        </div>
                    ) : collections.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                            No collections yet. Create one below!
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {collections.map((collection) => {
                                const IconComp = getIconComponent(collection.icon);
                                const isSelected = selectedCollections.includes(collection.id);

                                return (
                                    <button
                                        key={collection.id}
                                        onClick={() => handleToggle(collection.id)}
                                        className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all ${isSelected
                                            ? 'border-primary bg-primary/5'
                                            : 'border-gray-100 hover:border-gray-200'
                                            }`}
                                    >
                                        <div
                                            className="w-10 h-10 rounded-lg flex items-center justify-center"
                                            style={{ backgroundColor: collection.color + '20' }}
                                        >
                                            <IconComp size={20} style={{ color: collection.color }} />
                                        </div>
                                        <div className="flex-1 text-left">
                                            <p className="font-medium text-gray-900">{collection.name}</p>
                                            <p className="text-xs text-gray-500">
                                                {collection.file_count || 0} files
                                            </p>
                                        </div>
                                        <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all ${isSelected
                                            ? 'border-primary bg-primary'
                                            : 'border-gray-300'
                                            }`}>
                                            {isSelected && <Check size={14} className="text-white" />}
                                        </div>
                                    </button>
                                );
                            })}
                        </div>
                    )}

                    {/* Create New */}
                    <div className="mt-4 pt-4 border-t border-gray-100">
                        {showCreate ? (
                            <div className="flex gap-2">
                                <input
                                    type="text"
                                    value={newName}
                                    onChange={(e) => setNewName(e.target.value)}
                                    placeholder="Collection name"
                                    className="flex-1 px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none text-sm"
                                    autoFocus
                                    onKeyDown={(e) => e.key === 'Enter' && handleCreateCollection()}
                                />
                                <button
                                    onClick={handleCreateCollection}
                                    disabled={!newName.trim()}
                                    className="px-3 py-2 bg-primary text-white rounded-lg text-sm font-medium disabled:opacity-50"
                                >
                                    Add
                                </button>
                                <button
                                    onClick={() => {
                                        setShowCreate(false);
                                        setNewName('');
                                    }}
                                    className="px-3 py-2 text-gray-500 hover:bg-gray-100 rounded-lg text-sm"
                                >
                                    Cancel
                                </button>
                            </div>
                        ) : (
                            <button
                                onClick={() => setShowCreate(true)}
                                className="w-full flex items-center justify-center gap-2 py-2.5 text-primary hover:bg-primary/5 rounded-xl transition-colors font-medium"
                            >
                                <Plus size={18} />
                                Create New Collection
                            </button>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="p-4 border-t border-gray-100 flex justify-end gap-3">
                    <button
                        onClick={onClose}
                        className="px-5 py-2.5 text-gray-600 hover:bg-gray-100 rounded-xl transition-colors font-medium"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className="px-5 py-2.5 bg-gradient-to-r from-primary to-indigo-600 text-white rounded-xl hover:shadow-lg hover:shadow-primary/25 transition-all font-medium disabled:opacity-50"
                    >
                        {saving ? 'Saving...' : 'Save'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AddToCollectionModal;
